import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks';
import { useDocumentTitle } from '../hooks/core';

interface NotFoundPageProps {
  title?: string;
  message?: string;
}

export const NotFoundPage: React.FC<NotFoundPageProps> = ({
  title = 'Page Not Found',
  message = "The page you're looking for doesn't exist or has been removed.",
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useDocumentTitle(title);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 2,
      }}
    >
      <Paper
        sx={{
          p: 6,
          maxWidth: 500,
          width: '100%',
          background: 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
        }}
      >
        {/* SquareUp branded 404 grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 0.5,
            width: 120,
            height: 120,
            mx: 'auto',
            mb: 3,
          }}
        >
          {Array.from({ length: 16 }).map((_, i) => (
            <Box
              key={i}
              sx={{
                bgcolor: i === 5 || i === 6 || i === 9 || i === 10 ? 'primary.main' : 'grey.300',
                borderRadius: 0.5,
                opacity: i === 5 || i === 6 || i === 9 || i === 10 ? 1 : 0.4,
              }}
            />
          ))}
        </Box>

        <Typography
          variant="h1"
          sx={{
            fontSize: '5rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
          }}
        >
          404
        </Typography>

        <Typography variant="h5" gutterBottom color="text.primary">
          {title}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {message}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {isAuthenticated ? (
            <>
              <Button
                variant="contained"
                onClick={() => navigate('/boards')}
                sx={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)',
                }}
              >
                My Boards
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
              >
                Home
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)',
                }}
              >
                Go Home
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
            </>
          )}
        </Box>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        SquareUp - The ultimate sports squares board manager
      </Typography>
    </Box>
  );
};
